/**
 * React-Query hooks for each Stage 10 explanation endpoint. Every hook
 * is independent so a failure in one section never blanks the whole
 * page; the ``CoachingHome`` page renders section-level error states
 * for individual failures.
 */
import { useQueries, useQuery } from "@tanstack/react-query";

import {
  getExplanation,
  getLastSessionExplanation,
  getMissingInformationExplanation,
  getPlanChangeExplanation,
  getSafetyExplanation,
  getTodayExplanation,
  getTodayFocusExplanation,
  getTodayPurposeExplanation,
  getWeekComplianceExplanation,
} from "@/api/coachingApi";

const RETRY = (failureCount, error) => {
  if (error?.status === 401 || error?.status === 403) return false;
  return failureCount < 1;
};

const key = (intent, params = {}) =>
  ["coaching-explanation", intent, params.effective_date || "today",
    params.detail_level || "STANDARD"];

export const useExplanation = ({ intent, effectiveDate,
                                          detailLevel = "STANDARD",
                                          enabled = true } = {}) => {
  const params = {
    ...(effectiveDate ? { effective_date: effectiveDate } : {}),
    detail_level: detailLevel,
  };
  return useQuery({
    queryKey: key(intent, params),
    queryFn: ({ signal }) => getExplanation({ intent, ...params }, { signal }),
    enabled: enabled && Boolean(intent),
    staleTime: 30_000,
    retry: RETRY,
  });
};

const buildExplanationQueries = ({ effectiveDate, detailLevel }) => {
  const params = {
    ...(effectiveDate ? { effective_date: effectiveDate } : {}),
    detail_level: detailLevel || "STANDARD",
  };
  return [
    { intent: "TODAY_WORKOUT", fn: getTodayExplanation },
    { intent: "TODAY_WORKOUT_PURPOSE", fn: getTodayPurposeExplanation },
    { intent: "TODAY_WORKOUT_FOCUS", fn: getTodayFocusExplanation },
    { intent: "LAST_SESSION_RESULT", fn: getLastSessionExplanation },
    { intent: "WEEKLY_COMPLIANCE", fn: getWeekComplianceExplanation },
    { intent: "PLAN_CHANGE", fn: getPlanChangeExplanation },
    { intent: "SAFETY_SUSPENSION", fn: getSafetyExplanation },
    { intent: "MISSING_INFORMATION", fn: getMissingInformationExplanation },
  ].map(({ intent, fn }) => ({
    queryKey: key(intent, params),
    queryFn: ({ signal }) => fn(params, { signal }),
    staleTime: 30_000,
    retry: RETRY,
  }));
};

export const useCoachingExplanationBundle = ({ effectiveDate,
                                                            detailLevel }
                                                          = {}) => {
  const results = useQueries({
    queries: buildExplanationQueries({ effectiveDate, detailLevel }),
  });
  const [today, purpose, focus, lastSession, weeklyCompliance,
            planChange, safety, missing] = results;
  return {
    today, purpose, focus, lastSession, weeklyCompliance,
    planChange, safety, missing,
    all: results,
    isLoading: results.some((r) => r.isLoading),
  };
};
